import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight, ArrowUp, ArrowDown, ArrowLeft } from 'lucide-react';

const MazeGame = ({ onLevelChange }) => {
  const [level, setLevel] = useState(1);
  const [maze, setMaze] = useState([]);
  const [playerPos, setPlayerPos] = useState({ x: 0, y: 1 });
  const [gameWon, setGameWon] = useState(false);
  const moveIntervalRef = useRef(null);
  const currentMoveRef = useRef(null);

  const width = 20;
  const height = 15;

  const generateMaze = (level) => {
    const maze = Array(height).fill().map(() => Array(width).fill(1));
    
    // Set borders
    for (let i = 0; i < width; i++) {
      maze[0][i] = 1;
      maze[height-1][i] = 1;
    }
    for (let i = 0; i < height; i++) {
      maze[i][0] = 1;
      maze[i][width-1] = 1;
    }
    
    // Start and end positions
    maze[1][0] = 0;  // Start
    maze[height-2][width-1] = 0;  // End
    maze[height-2][width-2] = 0;  // Path to end

    // Create initial path
    let currentPos = { x: 0, y: 1 };
    const visited = new Set();
    const stack = [currentPos];

    while (stack.length > 0) {
      const current = stack[stack.length - 1];
      visited.add(`${current.x},${current.y}`);
      maze[current.y][current.x] = 0;

      const neighbors = [
        { x: current.x, y: current.y - 2 },
        { x: current.x, y: current.y + 2 },
        { x: current.x - 2, y: current.y },
        { x: current.x + 2, y: current.y }
      ].filter(n => 
        n.x >= 0 && n.x < width && 
        n.y >= 0 && n.y < height && 
        !visited.has(`${n.x},${n.y}`)
      );

      if (neighbors.length > 0) {
        const next = neighbors[Math.floor(Math.random() * neighbors.length)];
        maze[Math.floor((current.y + next.y) / 2)][Math.floor((current.x + next.x) / 2)] = 0;
        stack.push(next);
      } else {
        stack.pop();
      }
    }

    const numExtraPaths = Math.min(level * 3, Math.floor(width * height / 3));
    for (let i = 0; i < numExtraPaths; i++) {
      const x = Math.floor(Math.random() * (width - 2)) + 1;
      const y = Math.floor(Math.random() * (height - 2)) + 1;
      if (maze[y][x] === 1) {
        maze[y][x] = 0;
      }
    }

    let x = width - 2;
    let y = height - 2;
    while (x > 0) {
      maze[y][x] = 0;
      if (Math.random() < 0.3 && y > 1) {
        maze[y-1][x] = 0;
        y--;
      }
      x--;
    }

    return maze;
  };

  const movePlayer = (dx, dy) => {
    if (gameWon) return;

    const newPos = {
      x: playerPos.x + dx,
      y: playerPos.y + dy
    };

    if (newPos.x === width && newPos.y === height - 2) {
      setGameWon(true);
      setTimeout(() => {
        setLevel(level + 1);
        setGameWon(false);
      }, 1000);
      return;
    }

    if (maze[newPos.y]?.[newPos.x] === 0) {
      setPlayerPos(newPos);
    }
  };

  const startMoving = (dx, dy) => {
    movePlayer(dx, dy);
    moveIntervalRef.current = setInterval(() => movePlayer(dx, dy), 30);
  };

  const stopMoving = () => {
    if (moveIntervalRef.current) {
      clearInterval(moveIntervalRef.current);
      moveIntervalRef.current = null;
    }
  };

  useEffect(() => {
    setMaze(generateMaze(level));
    setPlayerPos({ x: 0, y: 1 });
    onLevelChange?.(level);
  }, [level, onLevelChange]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      switch (e.key) {
        case 'ArrowUp': movePlayer(0, -1); break;
        case 'ArrowDown': movePlayer(0, 1); break;
        case 'ArrowLeft': movePlayer(-1, 0); break;
        case 'ArrowRight': movePlayer(1, 0); break;
        default: return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [maze, playerPos, gameWon, level]);

  return (
    <div className="flex flex-col items-center gap-2 mt-4">
      {gameWon && (
        <div className="text-green-600 font-bold text-sm">Level complete!</div>
      )}

      <div className="relative">
        <div className="absolute -left-4 top-[6.67%] text-green-600">
          <ArrowRight size={16} />
        </div>
        <div className="absolute -right-4 top-[86.67%] text-red-600">
          <ArrowRight size={16} />
        </div>

        <div className="grid gap-0">
          {maze.map((row, y) => (
            <div key={y} className="flex">
              {row.map((cell, x) => (
                <div
                  key={`${x}-${y}`}
                  className={`w-4 h-4 md:w-5 md:h-5 flex items-center justify-center
                    ${cell === 1 ? 'bg-gray-800' : 'bg-white'}
                    border border-gray-300`}
                >
                  {playerPos.x === x && playerPos.y === y && (
                    <div className="w-2 h-2 md:w-2.5 md:h-2.5 rounded-full bg-blue-500" />
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 mt-1">
        <div />
        <button
          className="p-2 md:p-1.5 bg-gray-200 rounded-lg active:bg-gray-300 select-none"
          onMouseDown={() => startMoving(0, -1)}
          onMouseUp={stopMoving}
          onMouseLeave={stopMoving}
          onTouchStart={(e) => {
            e.preventDefault();
            startMoving(0, -1);
          }}
          onTouchEnd={(e) => {
            e.preventDefault();
            stopMoving();
          }}
        >
          <ArrowUp className="w-4.5 h-4.5 md:w-4.5 md:h-4.5 pointer-events-none" />
        </button>
        <div />
        <button
          className="p-2 md:p-1.5 bg-gray-200 rounded-lg active:bg-gray-300 select-none"
          onMouseDown={() => startMoving(-1, 0)}
          onMouseUp={stopMoving}
          onMouseLeave={stopMoving}
          onTouchStart={(e) => {
            e.preventDefault();
            startMoving(-1, 0);
          }}
          onTouchEnd={(e) => {
            e.preventDefault();
            stopMoving();
          }}
        >
          <ArrowLeft className="w-4.5 h-4.5 md:w-4.5 md:h-4.5 pointer-events-none" />
        </button>
        <div className="p-2 md:p-1.5" />
        <button
          className="p-2 md:p-1.5 bg-gray-200 rounded-lg active:bg-gray-300 select-none"
          onMouseDown={() => startMoving(1, 0)}
          onMouseUp={stopMoving}
          onMouseLeave={stopMoving}
          onTouchStart={(e) => {
            e.preventDefault();
            startMoving(1, 0);
          }}
          onTouchEnd={(e) => {
            e.preventDefault();
            stopMoving();
          }}
        >
          <ArrowRight className="w-4.5 h-4.5 md:w-4.5 md:h-4.5 pointer-events-none" />
        </button>
        <div />
        <button
          className="p-2 md:p-1.5 bg-gray-200 rounded-lg active:bg-gray-300 select-none"
          onMouseDown={() => startMoving(0, 1)}
          onMouseUp={stopMoving}
          onMouseLeave={stopMoving}
          onTouchStart={(e) => {
            e.preventDefault();
            startMoving(0, 1);
          }}
          onTouchEnd={(e) => {
            e.preventDefault();
            stopMoving();
          }}
        >
          <ArrowDown className="w-4.5 h-4.5 md:w-4.5 md:h-4.5 pointer-events-none" />
        </button>
        <div />
      </div>
    </div>
  );
};

export default MazeGame;